import { createAction, handleActions, Reducer } from 'redux-actions';

import { keysToCamelCase } from '@adjust/dashboard-core/lib/fp';
import { hasRoleLevel } from '@adjust/dashboard-core/lib/utils/user';
import { Account, User, Timezone, UserRole, AccountPaymentPlan } from '@adjust/dashboard-core/api/types';
import { RootState } from '@redux/types';

export interface GlobalState {
  account: Nullable<Account>;
  user: Nullable<User>;
  timezones: Nullable<Timezone[]>;
}

type GlobalReducer<T = GlobalState> = Reducer<GlobalState, T>;

export const initialState: GlobalState = {
  account: null,
  user: null,
  timezones: null,
};

// Constants

export const defaultContractLength = 12;

// Actions

export const ACCOUNT_SET = 'global/ACCOUNT_SET';
export const USER_SET = 'global/USERS_SET';
export const TIMEZONES_SET = 'global/TIMEZONES_SET';
export const RESET_STATE = 'global/RESET_STATE';

// Payloads

type SetAccountPayload = Account;
type SetUserPayload = User;
type SetTimezonesPayload = Timezone[];

// Action creators

export const setAccount = createAction<SetAccountPayload>(ACCOUNT_SET);
export const setUser = createAction<SetUserPayload>(USER_SET);
export const setTimezones = createAction<SetTimezonesPayload>(TIMEZONES_SET);
export const resetState = createAction(RESET_STATE);

// Reducer

const handleSetAccount: GlobalReducer<SetAccountPayload> = (state, { payload }) => ({
  ...state,
  account: payload,
});

const handleSetUser: GlobalReducer<SetUserPayload> = (state, { payload }) => ({
  ...state,
  user: payload,
});

const handleSetTimezones: GlobalReducer<SetTimezonesPayload> = (state, { payload }) => ({
  ...state,
  timezones: payload,
});

const handleResetState = () => ({
  ...initialState,
});

export default handleActions<GlobalState, any>(
  {
    [ACCOUNT_SET]: handleSetAccount,
    [USER_SET]: handleSetUser,
    [TIMEZONES_SET]: handleSetTimezones,
    [RESET_STATE]: handleResetState,
  },
  initialState,
);

// Selectors

export const getCurrentUser = (state: RootState) => state.global.user;

export const getCurrentAccount = (state: RootState) => state.global.account;

export const getTimezones = (state: RootState) => state.global.timezones;

type GetAccountCamelCaseReturnType = {
  id: string;
  paymentPlan: AccountPaymentPlan;
  contractLength: Nullable<number>;
};

type GetAccountCamelCase = (state: RootState) => GetAccountCamelCaseReturnType;

export const getAccount: GetAccountCamelCase = (state: RootState) =>
  keysToCamelCase(state.global.account) as GetAccountCamelCaseReturnType;

export const getAccountNonCamelCase = (state: RootState) => state.global.account;

export const getContractLength = (state: RootState) => {
  const account = getAccount(state);

  return account?.contractLength || defaultContractLength;
};

// utils

export const hasUserRole = (state: RootState, role: UserRole) => {
  const { user } = state.global;
  return Boolean(user) && hasRoleLevel(user, role);
};
